/* =========================
   Base URL
   ========================= */
const RAW_BASE = import.meta.env.VITE_API_URL as string | undefined;
const BASE_URL = RAW_BASE ? RAW_BASE.replace(/\/+$/, "") : undefined;
export const API = BASE_URL;

/* =========================
   Errors + Options
   ========================= */
export class ApiError extends Error {
  status: number;
  payload?: unknown;
  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** The `error` string an API error body carries, when it has one. */
function errorMessageOf(payload: unknown): string | undefined {
  if (payload && typeof payload === "object" && "error" in payload) {
    const e = (payload as { error?: unknown }).error;
    if (typeof e === "string" && e.trim()) return e;
  }
  return undefined;
}

function normalizePath(path: string): string {
  // Ensure leading slash
  const p = path.startsWith("/") ? path : `/${path}`;
  // Remove double slashes EXCEPT after "https://"
  return p.replace(/([^:]\/)\/+/g, "$1");
}

/* =========================
   Auth Token Management
   ========================= */
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem("authToken", token);
  } else {
    localStorage.removeItem("authToken");
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!authToken) {
    authToken = localStorage.getItem("authToken");
  }
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

/* =========================
   401 handling — expired/invalid session
   ========================= */
// Endpoints where a 401 is a normal response (bad credentials), not a dead session
const AUTH_401_WHITELIST = [
  "/api/auth/login",
  "/api/auth/signup",
  "/api/auth/change-password",
];

function handleUnauthorized(path: string) {
  if (AUTH_401_WHITELIST.some((p) => path.startsWith(p))) return;
  // Token is expired or invalid — clear the dead session and send to login
  setAuthToken(null);
  localStorage.removeItem("authUser");
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

/* =========================
   Core JSON Fetch Wrapper
   ========================= */
export async function api<T = unknown>(
  path: string,
  { method = "GET", body, headers = {}, signal }: ApiOptions = {}
): Promise<T> {
  if (!BASE_URL) {
    throw new Error("VITE_API_URL is not set. Add it to your .env");
  }

  const cleanPath = normalizePath(path);
  const url = `${BASE_URL}${cleanPath}`;

  const authHeaders = await getAuthHeaders();

  const finalHeaders: HeadersInit = {
    Accept: "application/json",
    ...(body != null ? { "Content-Type": "application/json" } : {}),
    ...authHeaders,
    ...headers,
  };

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: body != null ? JSON.stringify(body) : undefined,
      mode: "cors",
      credentials: "omit",
      signal,
    });
  } catch (err: unknown) {
    // A cancelled request is not a network failure: callers check for
    // AbortError to ignore it, so it must keep its name.
    if (err instanceof Error && err.name === "AbortError") throw err;
    throw new ApiError(err instanceof Error ? err.message : "Network error", 0);
  }

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  const payload = isJson
    ? await res.json().catch(() => undefined)
    : await res.text().catch(() => "");

  if (!res.ok) {
    if (res.status === 401) handleUnauthorized(cleanPath);

    const msg =
      (isJson && errorMessageOf(payload)) ||
      (typeof payload === "string" && payload.trim()) ||
      `HTTP ${res.status}`;

    throw new ApiError(msg, res.status, payload);
  }

  return (isJson ? (payload as T) : (payload as unknown as T));
}

/* =========================
   Raw fetch (non-JSON)
   ========================= */
export async function apiRaw(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  if (!BASE_URL) throw new Error("VITE_API_URL missing");

  const cleanPath = normalizePath(path);
  const url = `${BASE_URL}${cleanPath}`;

  const authHeaders = await getAuthHeaders();

  const res = await fetch(url, {
    mode: "cors",
    credentials: "omit",
    ...init,
    headers: {
      ...(init.headers || {}),
      ...authHeaders,
    },
  });

  if (!res.ok) {
    if (res.status === 401) handleUnauthorized(cleanPath);

    let message = `HTTP ${res.status}`;
    try {
      const maybeJson: unknown = await res.clone().json();
      const fromBody = errorMessageOf(maybeJson);
      if (fromBody) message = fromBody;
    } catch {
      // Body was not JSON; the generic HTTP message stands.
    }
    throw new ApiError(message, res.status);
  }

  return res;
}

/* =========================
   Auth
   ========================= */
export type SafeUser = {
  id: string | number;
  name: string;
  email: string;
  logo_path?: string | null;
};

export async function meRequest(): Promise<{ user: SafeUser }> {
  return api("/api/auth/me");
}

export async function loginRequest(
  email: string,
  password: string
): Promise<{ token: string; user: SafeUser }> {
  return api("/api/auth/login", { method: "POST", body: { email, password } });
}

export async function registerRequest(
  name: string,
  email: string,
  password: string
): Promise<{ token: string; user: SafeUser }> {
  return api("/api/auth/signup", {
    method: "POST",
    body: { name, email, password },
  });
}

/* =========================
   Public demo
   ========================= */
export type DemoSession = { token: string; user: SafeUserLike; demo: true };
type SafeUserLike = { id: number; name: string; email: string; logo_path?: string | null };

/** Signs in to the shared demo account. No credentials: that is the point. */
/**
 * Nudge the API awake, fire and forget.
 *
 * Render's free plan stops the service after 15 minutes idle, and the next
 * request waits ~30 seconds while it boots. Calling this when someone lands on
 * a page starts that boot immediately, so by the time they click Sign in or
 * See the live demo the server is usually already up. Failures are ignored on
 * purpose: the real request that follows is what reports errors.
 */
let prewarmStarted = false;
export function prewarmApi(): void {
  if (prewarmStarted || !BASE_URL) return;
  prewarmStarted = true;
  try {
    void fetch(`${BASE_URL}/`, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
    }).catch(() => {
      // Offline, blocked, or still booting — nothing useful to do here.
    });
  } catch {
    // fetch itself threw (very old browser); not worth surfacing.
  }
}

export async function startDemoSession(): Promise<DemoSession> {
  return api("/api/demo/login", { method: "POST" });
}

/* =========================
   Password reset
   ========================= */
export async function requestPasswordReset(email: string): Promise<{ ok: true }> {
  return api("/api/auth/forgot-password", { method: "POST", body: { email } });
}

export async function resetPassword(token: string, password: string): Promise<{ ok: true }> {
  return api("/api/auth/reset-password", { method: "POST", body: { token, password } });
}

/* =========================
   Profile (company details printed on proposals)
   ========================= */
export type Profile = {
  id: number;
  name: string;
  email: string;
  logo_path?: string | null;
  company_name: string | null;
  company_phone: string | null;
  company_email: string | null;
  company_address: string | null;
  license_number: string | null;
  proposal_valid_days: number;
  proposal_terms: string | null;
};

export async function getProfile(): Promise<Profile> {
  const { user } = await api<{ user: Profile }>("/api/auth/me");
  return user;
}

export async function updateProfile(
  input: Partial<Omit<Profile, "id" | "email" | "logo_path">>
): Promise<Profile> {
  const { user } = await api<{ user: Profile }>("/api/auth/profile", { method: "PATCH", body: input });
  return user;
}

/* =========================
   Projects
   ========================= */
export interface Project {
  id: number;
  name: string;
  description?: string | null;
  location?: string | null;
  client_name?: string | null;
  client_phone?: string | null;
  client_email?: string | null;
  created_at: string;
  updated_at: string;
}

export type ClientDetails = {
  client_name?: string | null;
  client_phone?: string | null;
  client_email?: string | null;
};

export async function updateProject(
  id: number,
  input: ClientDetails & { name?: string; description?: string; location?: string }
): Promise<Project> {
  return api<Project>(`/api/projects/${id}`, { method: "PATCH", body: input });
}

export async function getProjects(): Promise<Project[]> {
  const { projects } = await api<{ projects: Project[] }>("/api/projects");
  return projects;
}

export async function createProject(input: {
  name: string;
  description: string;
  location: string;
} & ClientDetails): Promise<Project> {
  const { project } = await api<{ project: Project }>("/api/projects", {
    method: "POST",
    body: input,
  });
  return project;
}

export async function deleteProject(id: number): Promise<{ ok: boolean }> {
  return api(`/api/projects/${id}`, { method: "DELETE" });
}

/* =========================
   Dashboard
   ========================= */
export interface DashboardSummary {
  /** Drafts and sent quotes — not yet won, so not in contractValue. */
  pipelineValue?: number;
  totalProjects: number;
  totalEstimates: number;
  totalEstimateValue: number;
  totalExpenses: number;
  totalApprovedChangeOrders: number;
  contractValue: number;
  grossProfit: number;
}

export async function getDashboardSummary(): Promise<DashboardSummary> {
  return api("/api/dashboard/summary");
}

export interface DashboardProjectFinancial {
  id: number;
  name: string;
  city: string | null;
  state: string | null;
  created_at: string;
  estimatesTotal: number;
  expensesTotal: number;
  approvedChangeOrdersTotal: number;
  contractValue: number;
  grossProfit: number;
}

export async function getDashboardProjectsFinancial(): Promise<{
  projects: DashboardProjectFinancial[];
}> {
  return api("/api/dashboard/projects");
}

/* =========================
   Assemblies + Estimates
   ========================= */
export type EstimateStatus = "DRAFT" | "SENT" | "APPROVED" | "REJECTED";

export interface EstimateSummary {
  id: string;
  title?: string | null;
  status: EstimateStatus;
  subtotal?: number;
  tax?: number;
  total?: number;
  createdAt?: string;
  updatedAt?: string;
  project?: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
  };
}

export async function listAssemblies() {
  return api("/api/assemblies");
}

export async function listTemplates() {
  return api("/api/assemblies/templates");
}

export async function createEstimate(payload: {
  projectId: number;
  title?: string;
  location?: Record<string, unknown>;
  lines: Array<{ assemblyId: string; inputs: Record<string, number> }>;
}) {
  return api("/api/estimates", { method: "POST", body: payload });
}

export async function getEstimate(estimateId: string) {
  return api(`/api/estimates/${encodeURIComponent(estimateId)}`);
}

export async function listEstimates(projectId: number) {
  return api(`/api/projects/${projectId}/estimates`);
}

/** List ALL estimates for the authenticated user across all projects */
export async function listAllEstimates(): Promise<{ estimates: EstimateSummary[] }> {
  return api("/api/estimates");
}

/** Update the status of an estimate */
export async function updateEstimateStatus(
  estimateId: string,
  status: EstimateStatus
): Promise<{ estimate: EstimateSummary }> {
  return api(`/api/estimates/${encodeURIComponent(estimateId)}/status`, {
    method: "PATCH",
    body: { status },
  });
}

/** Update the title of an estimate */
export async function updateEstimateTitle(
  estimateId: string,
  title: string
): Promise<{ estimate: EstimateSummary }> {
  return api(`/api/estimates/${encodeURIComponent(estimateId)}/title`, {
    method: "PATCH",
    body: { title },
  });
}

/* =========================
   FIXED finalizeEstimate()
   ========================= */
export async function deleteEstimate(estimateId: string): Promise<{ ok: boolean }> {
  return api(`/api/estimates/${encodeURIComponent(estimateId)}`, { method: "DELETE" });
}

export async function finalizeEstimate(estimateId: string): Promise<void> {
  const path = normalizePath(
    `/api/estimates/${encodeURIComponent(estimateId)}/finalize`
  );

  return api(path, { method: "POST" });
}

/* =========================
   Proposal PDFs
   ========================= */
// getProposalPdfUrl was removed in v1. The PDF endpoint now requires an
// Authorization header, so a bare URL is useless to a browser and only invites
// someone to drop it into an <a href> again. Use downloadProposalPdf().

export async function downloadProposalPdf(estimateId: string): Promise<Blob> {
  const res = await apiRaw(`/api/proposals/${encodeURIComponent(estimateId)}.pdf`);
  return res.blob();
}

/* =========================
   Company Logo
   ========================= */
export interface LogoSignResponse {
  signedUrl: string;
  token: string;
  path: string;
  expiresIn: number;
}

export interface LogoInfo {
  logo_path: string | null;
  url: string | null;
}

export async function signLogoUpload(
  filename: string,
  mimeType: string
): Promise<LogoSignResponse> {
  return api<LogoSignResponse>("/api/uploads/logo-sign", {
    method: "POST",
    body: { filename, mime_type: mimeType },
  });
}

/** PUT the file straight to Supabase storage via the signed URL. */
export async function uploadLogoFile(signedUrl: string, file: File): Promise<void> {
  const res = await fetch(signedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type, "x-upsert": "true" },
    body: file,
  });
  if (!res.ok) throw new ApiError(`Logo upload failed (HTTP ${res.status})`, res.status);
}

export async function commitLogo(path: string): Promise<LogoInfo> {
  return api<LogoInfo>("/api/uploads/logo-commit", {
    method: "POST",
    body: { path },
  });
}

export async function getLogo(): Promise<LogoInfo> {
  return api<LogoInfo>("/api/uploads/logo");
}

export async function deleteLogo(): Promise<void> {
  await api("/api/uploads/logo", { method: "DELETE" });
}

/* =========================
   Expenses + Budget
   ========================= */
export async function getBudgetReport(projectId: number) {
  return api(`/api/reports/budget?projectId=${projectId}`);
}

export async function listExpenses(projectId: number) {
  return api(`/api/expenses?projectId=${projectId}`);
}

export interface ExpenseInput {
  projectId: number;
  description?: string;
  amount: number;
  date?: string;
  category?: string;
  vendor?: string;
  currency?: string;
  receiptUrl?: string;
  notes?: string;
}

export async function createExpense(input: ExpenseInput) {
  return api("/api/expenses", {
    method: "POST",
    body: { ...input, currency: input.currency || "USD" },
  });
}

export async function deleteExpense(id: string | number): Promise<{ ok: boolean }> {
  return api(`/api/expenses/${encodeURIComponent(String(id))}`, { method: "DELETE" });
}

export async function updateExpense(
  id: string | number,
  data: { category?: string; vendor?: string; description?: string; amount?: number; date?: string }
): Promise<Record<string, unknown>> {
  return api(`/api/expenses/${encodeURIComponent(String(id))}`, { method: "PATCH", body: data });
}

/* =========================
   Change Orders
   ========================= */
export interface ChangeOrder {
  id: string;
  projectId: number;
  estimateId?: string | null;
  title: string;
  description?: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  amount: number;
  createdAt?: string;
  decidedAt?: string | null;
}

export async function listChangeOrders(projectId: number): Promise<ChangeOrder[]> {
  const data = await api<unknown>(`/api/change-orders?projectId=${projectId}`);
  if (Array.isArray(data)) return data as ChangeOrder[];
  const wrapped = data as { changeOrders?: ChangeOrder[] };
  return wrapped.changeOrders ?? [];
}

export async function createChangeOrder(input: {
  projectId: number;
  title: string;
  description?: string;
  amount: number;
  estimateId?: string;
}): Promise<ChangeOrder> {
  return api("/api/change-orders", { method: "POST", body: input });
}

export async function approveChangeOrder(id: string): Promise<ChangeOrder> {
  return api(`/api/change-orders/${encodeURIComponent(id)}/approve`, { method: "POST" });
}

export async function rejectChangeOrder(id: string): Promise<ChangeOrder> {
  return api(`/api/change-orders/${encodeURIComponent(id)}/reject`, { method: "POST" });
}

/* =========================
   User Price Sheet
   ========================= */
export type ItemKind = "MATERIAL" | "EQUIPMENT" | "LABOR";
export type ContractType = "LUMP_SUM" | "TIME_AND_MATERIALS";

export interface PriceSheetItem {
  id: string | null;
  category: string;
  itemName: string;
  unit: string;
  /** MATERIAL: what you pay (tax included). LABOR: what you charge. EQUIPMENT: rental cost. */
  unitPrice: number;
  kind: ItemKind;
  notes: string | null;
  /** You changed this built-in item's price or unit. */
  isCustomized: boolean;
  /** You added this item yourself. */
  isUserItem: boolean;
  /** @deprecated use isCustomized */
  isCustom: boolean;
}

export interface PricingSettings {
  /** 0.20 = 20% */
  materialMarkupPct: number;
  /** 0.09375 = 9.375%; null until set. Required for time-and-materials quotes. */
  salesTaxRate: number | null;
  defaultContractType: ContractType;
}

export async function getPriceSheet(): Promise<{ sheet: PriceSheetItem[]; settings: PricingSettings }> {
  return api("/api/pricing/sheet");
}

export interface PriceSheetSaveItem {
  category: string;
  itemName: string;
  unit: string;
  unitPrice: number;
  kind?: ItemKind;
  notes?: string | null;
}

/** Saves only the rows you pass — send changed and new rows, not the whole sheet. */
export async function savePriceSheet(items: PriceSheetSaveItem[]): Promise<{ ok: boolean; sheet: PriceSheetItem[] }> {
  return api("/api/pricing/sheet", { method: "PUT", body: { items } });
}

export async function deletePriceSheetItem(id: string): Promise<{ ok: boolean }> {
  return api(`/api/pricing/sheet/items/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Restores built-in prices. Items you added are kept. */
export async function resetPriceSheet(): Promise<{ ok: boolean; sheet: PriceSheetItem[] }> {
  return api("/api/pricing/sheet/reset", { method: "POST" });
}

export async function savePricingSettings(settings: PricingSettings): Promise<{ settings: PricingSettings }> {
  return api("/api/pricing/settings", { method: "PUT", body: settings });
}

/* =========================
   Estimates from the price sheet
   ========================= */
export interface QuoteRow {
  category: string;
  itemName: string;
  unit: string;
  kind: ItemKind;
  qty: number;
  unitCost: number;   // from your sheet (never shown to the customer)
  unitPrice: number;  // what the customer pays per unit
  extended: number;
  basis?: string;
}

export interface Quote {
  contractType: ContractType;
  materialMarkupPct: number;
  salesTaxRate: number | null;
  sections: Array<{ category: string; rows: QuoteRow[]; total: number }>;
  materialsTotal: number;
  equipmentTotal: number;
  laborTotal: number;
  subtotal: number;
  taxableBase: number;
  tax: number;
  total: number;
  materialsCost: number;
  markupAmount: number;
}

export interface SheetEstimate {
  id: string;
  title: string | null;
  subtotal: number | string;
  tax: number | string;
  total: number | string;
  contractType: ContractType;
  quote: Quote;
}

/* =========================
   Editing an estimate
   ========================= */
export type RowSource = "price-sheet" | "override" | "custom";

export interface EditableRow {
  category: string;
  itemName: string;
  unit: string;
  kind: ItemKind;
  qty: number;
  /** Your cost (materials, equipment) or your rate (labor) for this job. */
  unitCost: number;
  source: RowSource;
  /** For an override: the price it replaced. */
  baseCost?: number;
  basis?: string;
}

export interface EstimateDetail {
  id: string;
  title: string | null;
  status: EstimateStatus;
  contractType: ContractType;
  materialMarkupPct: number | null;
  salesTaxRate: number | null;
  subtotal: number;
  tax: number;
  total: number;
  project: { id: number; name: string } | null;
  editable: boolean;
  /** Made before editing existed (assembly-based); read-only. */
  legacy: boolean;
  rows: EditableRow[] | null;
}

export async function getEstimateDetail(id: string): Promise<EstimateDetail> {
  return api(`/api/estimates/${encodeURIComponent(id)}`);
}

export type EditRowInput =
  | { category: string; itemName: string; qty: number; unitCost?: number; basis?: string }
  | { custom: true; category: string; itemName: string; unit: string; kind: ItemKind; qty: number; unitCost: number };

export async function saveEstimateRows(
  id: string,
  body: { contractType: ContractType; rows: EditRowInput[] }
): Promise<{ id: string; status: EstimateStatus; quote: Quote; rows: EditableRow[] }> {
  return api(`/api/estimates/${encodeURIComponent(id)}/rows`, { method: "PUT", body });
}

export interface RepricePreview {
  dryRun: true;
  changes: Array<{ category: string; itemName: string; from: number; to: number }>;
  missing: string[];
  markupFrom: number | null;
  markupTo: number;
  totalFrom: number;
  totalTo: number;
}

export async function previewReprice(id: string): Promise<RepricePreview> {
  return api(`/api/estimates/${encodeURIComponent(id)}/reprice`, { method: "POST", body: { dryRun: true } });
}

export async function applyReprice(id: string): Promise<{ status: EstimateStatus; quote: Quote }> {
  return api(`/api/estimates/${encodeURIComponent(id)}/reprice`, { method: "POST", body: {} });
}

/** Send which items and how many — prices always come from your sheet on the server. */
export async function createEstimateFromSheet(input: {
  projectId: number;
  title?: string;
  contractType: ContractType;
  rows: Array<{ category: string; itemName: string; qty: number; basis?: string }>;
}): Promise<SheetEstimate> {
  return api("/api/estimates/from-sheet", { method: "POST", body: input });
}

/* =========================
   Convenience alias
   ========================= */
export const authedFetch = api;
